import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function TermsOfService() {
  return (
    <>
      <SEO
        title="Terms of Service - Kecly"
        description="Read Kecly's terms of service to understand the terms and conditions for using our services and website."
        keywords="terms of service, legal terms, Kecly conditions"
      />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="font-orbitron font-bold text-4xl md:text-5xl gradient-text mb-8">
              Terms of Service
            </h1>
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-muted-foreground mb-6">
                <strong>Welcome to Kecly!</strong> These Terms of Service outline the rules and regulations for using our website and digital services. Please read them carefully to understand your rights and responsibilities when working with us. If you have any questions, check our FAQ below or contact us directly.
              </p>
              <p className="text-muted-foreground mb-6">
                Last updated: December 2024
              </p>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Acceptance of Terms</h2>
                <p className="text-muted-foreground mb-4">
                  By accessing and using the services provided by Kecly ("we," "our," or "us"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Services Description</h2>
                <p className="text-muted-foreground mb-4">
                  Kecly provides software development, web development, mobile app development, digital marketing, and related technology services. Our services include but are not limited to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>Custom software development and consulting</li>
                  <li>Web design and development services</li>
                  <li>Mobile application development</li>
                  <li>Digital marketing and SEO services</li>
                  <li>Cloud services and DevOps implementation</li>
                  <li>Maintenance and technical support</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Payment Terms</h2>
                <h3 className="font-orbitron font-medium text-lg mb-3">Project-Based Services</h3>
                <p className="text-muted-foreground mb-4">
                  For project-based services, payment terms will be specified in the individual project agreement. Typically, payments are structured as follows:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>50% upfront payment before project commencement</li>
                  <li>Remaining 50% upon project completion and delivery</li>
                  <li>For large projects, milestone-based payments may be arranged</li>
                </ul>

                <h3 className="font-orbitron font-medium text-lg mb-3">Recurring Services</h3>
                <p className="text-muted-foreground mb-4">
                  For ongoing services such as maintenance, hosting, or support:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>Monthly payments are due in advance</li>
                  <li>Annual subscriptions receive a discount as specified</li>
                  <li>Services may be suspended for non-payment after 7 days notice</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Intellectual Property Rights</h2>
                <p className="text-muted-foreground mb-4">
                  Upon full payment for services, clients will own the intellectual property rights to custom-developed software, websites, and applications, except for:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>Pre-existing intellectual property owned by Kecly</li>
                  <li>Third-party libraries, frameworks, and components</li>
                  <li>General methodologies and business processes</li>
                  <li>Any improvements to our existing tools and technologies</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Warranties and Disclaimers</h2>
                <p className="text-muted-foreground mb-4">
                  Kecly warrants that services will be performed in a professional manner consistent with industry standards. However:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>We do not guarantee specific results or outcomes</li>
                  <li>Software is provided "as is" without warranty of any kind</li>
                  <li>We are not liable for third-party service interruptions</li>
                  <li>Client is responsible for data backup and security</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Limitation of Liability</h2>
                <p className="text-muted-foreground mb-4">
                  Kecly's liability for any claim related to our services shall not exceed the total amount paid by the client for the specific service giving rise to the claim. We shall not be liable for:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>Indirect, incidental, or consequential damages</li>
                  <li>Loss of profits, data, or business opportunities</li>
                  <li>Damages resulting from client's misuse of services</li>
                  <li>Third-party actions or software conflicts</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Service Level Agreements</h2>
                <p className="text-muted-foreground mb-4">
                  For services covered by a Service Level Agreement (SLA):
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>Response times and availability guarantees are as specified in the SLA</li>
                  <li>Service credits may be provided for SLA breaches</li>
                  <li>Emergency support is available for critical issues</li>
                  <li>Planned maintenance windows are excluded from SLA calculations</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Termination</h2>
                <p className="text-muted-foreground mb-4">
                  Either party may terminate services under the following conditions:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>30 days written notice for ongoing services</li>
                  <li>Immediate termination for material breach of contract</li>
                  <li>Non-payment after 30 days of written notice</li>
                  <li>Mutual agreement between both parties</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Confidentiality</h2>
                <p className="text-muted-foreground mb-4">
                  Kecly agrees to maintain the confidentiality of all client information and data. We will not disclose, share, or use client confidential information except as necessary to provide the agreed services or as required by law.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Governing Law</h2>
                <p className="text-muted-foreground mb-4">
                  These terms shall be governed by and construed in accordance with the laws of the United Arab Emirates. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts of Sharjah, UAE.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Changes to Terms</h2>
                <p className="text-muted-foreground mb-4">
                  Kecly reserves the right to modify these terms at any time. We will notify clients of any material changes via email or through our website. Continued use of our services after notification constitutes acceptance of the modified terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Contact Information</h2>
                <p className="text-muted-foreground mb-4">
                  For questions about these Terms of Service, please contact us:
                </p>
                <div className="text-muted-foreground">
                  <p>Email: info@kecly.com</p>
                  <p>Phone: +971-58-248-1443</p>
                  <p>Address: Almawarid Building 209, Industrial Area 3, Sharjah, UAE</p>
                </div>
              </section>

              <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                  <WebDesignLocations />
                </div>
              </section>

              {/* FAQ Section for SEO */}
              <section className="faq-section bg-white py-12">
                <div className="container mx-auto px-6">
                  <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold">What are Terms of Service?</h3>
                      <p>Terms of Service are the legal agreements between Kecly and our clients or website users, outlining the rules, responsibilities, and rights of both parties.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold">How do I accept the Terms of Service?</h3>
                      <p>By using our website or engaging our services, you automatically agree to these terms. For project work, acceptance is usually confirmed via email or signed agreement.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold">Can the Terms of Service change?</h3>
                      <p>Yes, Kecly may update these terms as needed. We will notify clients of material changes via email or website updates.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold">Who can I contact for legal questions?</h3>
                      <p>You can email info@kecly.com or call +971-58-248-1443 for any questions about our terms, privacy, or legal matters.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Internal Links for SEO */}
              <section className="internal-links-section bg-gray-50 py-8">
                <div className="container mx-auto px-6 text-center">
                  <span className="font-semibold">Explore more:</span>
                  <div className="flex flex-wrap gap-4 justify-center mt-2">
                    <a href="/" className="underline text-blue-700">Home</a>
                    <a href="/services" className="underline text-blue-700">Services</a>
                    <a href="/privacy-policy" className="underline text-blue-700">Privacy Policy</a>
                    <a href="/contact" className="underline text-blue-700">Contact Us</a>
                  </div>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
